import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { trackAnalyticsEvent } from '../../actions/analytics';
import { ANALYTICS_EVENTS } from '../../lib/analytics';
import { submitCandidateStories } from './submit';

// ECDEX-3840 (slice 5): what the `candidateStories` Redux slice held, per
// report. `stories` are the ones the last submit sent back (the hook falls
// back to the report's own); `submitted` is
// whether to show the success or failure screen instead of the form. A failed
// submit counts as submitted: the modal shows its failure screen for
// `submitted && submitError`.
export interface CandidateStoriesCacheData {
  stories: any[];
  submitted: boolean;
  submitError: boolean;
}

export const candidateStoriesQueryKey = (reportId?: string) => [
  'candidateStories',
  reportId,
];
const submitMutationKey = (reportId?: string) => [
  'candidateStories',
  'submit',
  reportId,
];

// Whether a submit for the report is running.
export const useCandidateStoriesProcessing = (reportId?: string) =>
  useIsMutating({ mutationKey: submitMutationKey(reportId) }) > 0;

const initialData: CandidateStoriesCacheData = {
  stories: [],
  submitted: false,
  submitError: false,
};

// Reads the report's stories state, and starts it the way the report says: a
// report that already has stories counts as submitted, one that has none does
// not (even if a submit failed before the candidate came back).
export const useCandidateStorySubmission = (report: {
  id?: string;
  candidateStories?: any[];
}) => {
  const queryClient = useQueryClient();
  const key = candidateStoriesQueryKey(report.id);
  const hasStories = !!report.candidateStories?.length;

  // Written, not fetched: the data comes from `useSubmitCandidateStories`.
  const { data } = useQuery<CandidateStoriesCacheData>({
    queryKey: key,
    queryFn: () => initialData,
    enabled: false,
    staleTime: Infinity,
  });
  const processing = useCandidateStoriesProcessing(report.id);

  // Until the effect below has started this mount's state, the cache still
  // holds the last mount's (a failure, say): show what the report says.
  const [startedFor, setStartedFor] = useState<string | undefined>();
  const started = startedFor === report.id;
  useEffect(() => {
    queryClient.setQueryData<CandidateStoriesCacheData>(
      candidateStoriesQueryKey(report.id),
      previous => ({
        ...initialData,
        ...previous,
        submitted: hasStories,
      }),
    );
    setStartedFor(report.id);
  }, [queryClient, report.id, hasStories]);

  return {
    candidateStorySubmitted: started
      ? data?.submitted ?? hasStories
      : hasStories,
    submitError: data?.submitError ?? false,
    // What a submit sent back, else what the report already has.
    stories: data?.stories?.length
      ? data.stories
      : report.candidateStories ?? [],
    processing,
  };
};

// Submits the form. It never rejects: a failure is the failure screen.
export const useSubmitCandidateStories = (report: { id?: string }) => {
  const dispatch = useDispatch<any>();
  const queryClient = useQueryClient();
  const portalToken = useSelector((state: any) => state.portal?.token);
  const key = candidateStoriesQueryKey(report.id);

  const { mutateAsync } = useMutation({
    mutationKey: submitMutationKey(report.id),
    mutationFn: ({ values, jwt }: { values: any; jwt?: string | null }) =>
      submitCandidateStories(values, jwt, portalToken),
    onSuccess: response => {
      queryClient.setQueryData<CandidateStoriesCacheData>(key, {
        stories: response,
        submitted: true,
        submitError: false,
      });
      dispatch(trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_STORY_SHARED));
    },
    onError: () => {
      queryClient.setQueryData<CandidateStoriesCacheData>(key, previous => ({
        ...initialData,
        ...previous,
        submitted: true,
        submitError: true,
      }));
    },
  });

  return useMemo(
    () => (values: any, jwt?: string | null) =>
      mutateAsync({ values, jwt }).catch(() => undefined),
    [mutateAsync],
  );
};
